import { allocateReadingBudget, allowedReadingElapsed, inMainReadingViewport, intersectReadingRects, mergeAbsoluteReadingCredit,
  observeReadingPosition, readingWordSpans, selectReadingPosition, visibleReadingFraction, type ReadingCandidate,
  type ReadingPositionCandidate, type ReadingPositionDwell, type ReadingRect } from "./readingEstimator.ts";
import { readingDisplay } from "./readingDisplay.ts";

export interface ReadingSegment {
  index: number;
  wordCount: number;
  requiredMilliseconds: number;
  creditedMilliseconds: number;
}

export interface ReadingBlock { nodeId: string; hash: string; text: string; segments: ReadingSegment[]; manuallyRead?: boolean }
export interface ReadingPosition { nodeId: string; hash: string; segmentIndex: number; observedAt: string }
export interface ReadingResume {
  nodeId: string;
  segmentIndex: number;
  state: "exact" | "changed" | "nearby" | "estimated";
  sourceRevisionId: string;
  observedAt: string | null;
}
export interface ReadingManifest {
  documentId: string;
  revisionId: string;
  revisionNumber: number;
  latestRevisionId: string;
  wordsPerMinute: number;
  blocks: ReadingBlock[];
  progress: Record<string, unknown>;
  serverNow?: string;
  resume?: ReadingResume | null;
  blockChanges?: { nodeId: string; kind: "new" | "updated" }[];
}

interface WordRange { range: Range; exposure: number }
interface SegmentState extends ReadingSegment {
  key: string;
  nodeId: string;
  hash: string;
  acknowledged: number;
  words: WordRange[];
  range?: Range;
  block: HTMLElement;
}
interface CreditPayload { nodeId: string; hash: string; segmentIndex: number; creditedMilliseconds: number }
interface ViewedBlock { nodeId: string; hash: string }
interface Batch { credits: CreditPayload[]; viewedBlocks: ViewedBlock[]; position?: ReadingPosition; body: string; engagementVersion: number; serial: number }
interface Session {
  element: HTMLElement;
  signature: string;
  documentId: string;
  revisionId: string;
  url: string;
  manifest?: ReadingManifest;
  segments: Map<string, SegmentState>;
  locallyViewed: Set<string>;
  pendingViews: Map<string, ViewedBlock>;
  abort: AbortController;
  loading: boolean;
  disabled: boolean;
  invalidated: boolean;
  loadAttempt: number;
  lastReport: number;
  visitRequested: boolean;
  visited: boolean;
  sending: boolean;
  retry?: Batch;
  serial: number;
  dispatchedSerial: number;
  engagementVersion: number;
  engagedKey?: string;
  positionDwell?: ReadingPositionDwell;
  position?: ReadingPosition;
  acknowledgedPositionAt: number;
  serverClockBase: number;
  performanceClockBase: number;
}

interface ViewportObservation {
  session: Session;
  at: number;
  lastActivity: number;
  candidates: ReadingCandidate[];
  positions: ReadingPositionCandidate[];
  readingLine: number;
}

const initialized = new WeakSet<Document>();
const ignored = "[data-reading-ignore], [data-reading-badge], .q-link-status, .annotation-indicators, [data-annotation-indicators], [data-annotation-indicator], [data-annotation-anchor], [data-qprins-editor], .ProseMirror, script, style, template";
const selector = "[data-qprins-static-document][data-reading-document-id][data-reading-revision-id][data-reading-manifest-url]";
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const reportInterval = 12_000;

export function initializeReadingTracker(root: Document = document): void {
  if (initialized.has(root)) return;
  initialized.add(root);
  const view = root.defaultView;
  if (!view) return;
  let session: Session | undefined;
  let observation: ViewportObservation | undefined;
  const now = () => Math.floor(performance.now());
  let lastActivity = now();
  let printing = view.matchMedia("print").matches;
  let suspended = false;
  let mutationScheduled = false;
  let viewsScheduled = false;
  let displayScheduled = false;
  let marking = false;

  function activity() {
    const at = now();
    const wasIdle = at - lastActivity >= 60_000;
    if (wasIdle) settleReading(at);
    lastActivity = at;
    if (wasIdle) captureReading();
    else if (observation) observation.lastActivity = at;
  }
  function foreground(): boolean {
    return !suspended && !printing && root.visibilityState === "visible" && root.hasFocus();
  }

  function synchronize(): void {
    const element = root.querySelector<HTMLElement>(selector);
    const documentId = element?.dataset.readingDocumentId ?? "";
    const revisionId = element?.dataset.readingRevisionId ?? "";
    const manifestUrl = element?.dataset.readingManifestUrl ?? "";
    const signature = `${documentId}/${revisionId}/${manifestUrl}/${element?.dataset.revisionId ?? ""}`;
    if (session && (session.element !== element || session.signature !== signature)) {
      settleReading();
      void report(session, true);
      session.abort.abort();
      session = undefined;
    }
    if (!element || !uuid.test(documentId) || !uuid.test(revisionId)
      || (element.dataset.revisionId && element.dataset.revisionId !== revisionId)) return;
    let url: URL;
    try { url = new URL(manifestUrl, view!.location.href); } catch { return; }
    if (url.origin !== view!.location.origin || url.pathname !== `/ui/reading/pages/${documentId}/revisions/${revisionId}` || url.search || url.hash) return;
    if (!session) {
      session = { element, signature, documentId, revisionId, url: url.href, segments: new Map(),
        locallyViewed: new Set(), pendingViews: new Map(),
        abort: new AbortController(), loading: false, disabled: false, invalidated: true, loadAttempt: -Infinity,
        lastReport: -Infinity, visitRequested: false, visited: false, sending: false, serial: 0, dispatchedSerial: 0,
        engagementVersion: 0, acknowledgedPositionAt: -Infinity, serverClockBase: Date.now(), performanceClockBase: performance.now() };
    }
    if (!session.manifest && !session.loading && !session.disabled && performance.now() - session.loadAttempt >= reportInterval) void load(session);
  }

  async function load(current: Session): Promise<void> {
    current.loading = true;
    current.loadAttempt = performance.now();
    try {
      const response = await fetch(current.url, { credentials: "same-origin", signal: current.abort.signal, cache: "no-store" });
      if (response.status === 401 || response.status === 403) current.disabled = true;
      if (!response.ok) return;
      const manifest = await response.json() as ReadingManifest;
      if (session !== current || !current.element.isConnected || !validManifest(manifest, current)) return;
      current.manifest = manifest;
      const serverNow = Date.parse(manifest.serverNow ?? "");
      current.serverClockBase = Number.isFinite(serverNow) ? serverNow : Date.now();
      current.performanceClockBase = performance.now();
      bind(current);
      emitManifest(current, manifest);
      captureReading();
      scheduleViews();
    } catch { /* A later foreground tick retries transient transport failures. */ }
    finally { current.loading = false; }
  }

  function bind(current: Session): void {
    if (!current.manifest) return;
    current.invalidated = false;
    for (const block of current.manifest.blocks) {
      const element = current.element.querySelector<HTMLElement>(`#node-${CSS.escape(block.nodeId)}`);
      const words = element ? readingRangesForBlock(element, block.text) : undefined;
      if (!element || !words || words.length !== block.segments.reduce((sum, part) => sum + part.wordCount, 0)) {
        current.invalidated = true;
        continue;
      }
      let offset = 0;
      for (const segment of block.segments) {
        const key = `${block.nodeId}/${block.hash}/${segment.index}`;
        const existing = current.segments.get(key);
        const selected = words.slice(offset, offset + segment.wordCount);
        offset += segment.wordCount;
        const range = selected[0]?.cloneRange();
        if (range) range.setEnd(selected.at(-1)!.endContainer, selected.at(-1)!.endOffset);
        const baseline = mergeAbsoluteReadingCredit(existing?.creditedMilliseconds ?? 0, segment.creditedMilliseconds, segment.requiredMilliseconds);
        current.segments.set(key, { ...segment, key, nodeId: block.nodeId, hash: block.hash, block: element, range,
          creditedMilliseconds: baseline, acknowledged: Math.max(existing?.acknowledged ?? 0, segment.creditedMilliseconds),
          words: selected.map((word, index) => ({ range: word, exposure: existing?.words[index]?.exposure ?? 0 })) });
      }
    }
  }

  function emitManifest(current: Session, value: ReadingManifest): void {
    const hashes = new Map(value.blocks.map(block => [block.nodeId, block.hash]));
    const marked = new Set(current.manifest?.blocks.filter(block => block.manuallyRead).map(block => `${block.nodeId}/${block.hash}`));
    const previousMark = current.manifest?.progress.lastManualCompletion as { markedAt: string } | undefined;
    const nextMark = value.progress.lastManualCompletion as { markedAt: string } | undefined;
    const manifest = { ...value, blocks: value.blocks.map(block => ({ ...block, manuallyRead: block.manuallyRead || marked.has(`${block.nodeId}/${block.hash}`) })),
      progress: { ...value.progress, ...(previousMark && (!nextMark || Date.parse(previousMark.markedAt) > Date.parse(nextMark.markedAt))
        ? { lastManualCompletion: previousMark, manuallyCoveredPercent: current.manifest?.progress.manuallyCoveredPercent } : {}) },
      blockChanges: (value.blockChanges ?? [])
      .filter(change => !current.locallyViewed.has(`${change.nodeId}/${hashes.get(change.nodeId)}`)) };
    manifest.blockChanges = manifest.blockChanges.filter(change => !manifest.blocks.find(block => block.nodeId === change.nodeId)?.manuallyRead);
    current.manifest = manifest;
    publish(current);
  }

  function publish(current: Session): void {
    if (!current.manifest || session !== current || !current.element.isConnected) return;
    const manifest = readingDisplay(current.manifest, (block, index) =>
      current.segments.get(`${block.nodeId}/${block.hash}/${index}`)?.creditedMilliseconds ?? block.segments[index].creditedMilliseconds);
    root.dispatchEvent(new CustomEvent("qprins:reading-progress", { detail: manifest.progress }));
    root.dispatchEvent(new CustomEvent("qprins:reading-manifest", { detail: manifest }));
  }

  function scheduleDisplay(): void {
    if (displayScheduled) return;
    displayScheduled = true;
    view!.requestAnimationFrame(() => { displayScheduled = false; if (session) publish(session); });
  }

  function observeViews(): void {
    const current = session;
    if (!current?.manifest || current.disabled || current.invalidated || !foreground()) return;
    const viewport = readingViewport(root);
    const changed = new Set((current.manifest.blockChanges ?? []).map(change => change.nodeId));
    let updated = false;
    for (const block of current.manifest.blocks) {
      if (!changed.has(block.nodeId)) continue;
      const key = `${block.nodeId}/${block.hash}`;
      if (current.locallyViewed.has(key)) continue;
      const element = current.element.querySelector<HTMLElement>(`#node-${CSS.escape(block.nodeId)}`);
      const clip = element && visibleClip(element, root);
      if (!element || !clip || !inMainReadingViewport(element.getBoundingClientRect(), viewport, clip)) continue;
      current.locallyViewed.add(key);
      current.pendingViews.set(key, { nodeId: block.nodeId, hash: block.hash });
      updated = true;
    }
    if (updated) {
      current.visitRequested = true;
      emitManifest(current, current.manifest);
      void report(current);
    }
  }

  function scheduleViews(): void {
    if (viewsScheduled) return;
    viewsScheduled = true;
    view!.requestAnimationFrame(() => { viewsScheduled = false; observeViews(); });
  }

  function tick(): void {
    settleReading();
    synchronize();
    observeViews();
    captureReading();
    const current = session;
    if (current?.manifest && !current.disabled && !current.invalidated && foreground()) {
      current.visitRequested = true;
      if (now() - current.lastReport >= reportInterval) void report(current);
    }
  }

  function captureReading(): void {
    observation = undefined;
    const current = session;
    if (!current?.manifest || current.disabled || current.invalidated || !current.element.isConnected || !foreground()) {
      if (current) current.positionDwell = undefined;
      return;
    }
    const at = now();
    const viewport = readingViewport(root);
    const candidates: ReadingCandidate[] = [];
    const positionCandidates: ReadingPositionCandidate[] = [];
    const clips = new Map<Element, ReadingRect | undefined>();
    function clipFor(element: Element): ReadingRect | undefined {
      if (!clips.has(element)) {
        const bounds = intersectReadingRects(element.getBoundingClientRect(), viewport);
        clips.set(element, bounds.right > bounds.left && bounds.bottom > bounds.top ? visibleClip(element, root) : undefined);
      }
      return clips.get(element);
    }
    // bind() inserts blocks and segments in canonical reading order; keep that order for allocation.
    for (const state of current.segments.values()) {
      if (!state.range || state.creditedMilliseconds >= state.requiredMilliseconds) continue;
      const clip = clipFor(state.block);
      if (!clip || visibleReadingFraction([...state.range.getClientRects()], clip) <= 0) continue;
      let visible = 0;
      let exposed = 0;
      let top = Infinity;
      let bottom = -Infinity;
      for (const word of state.words) {
        const wordClip = clipFor(word.range.startContainer.parentElement ?? state.block);
        const rectangles = [...word.range.getClientRects()];
        const portion = wordClip ? visibleReadingFraction(rectangles, wordClip) : 0;
        if (portion > 0 && wordClip) for (const rectangle of rectangles) {
          const exposedRect = intersectReadingRects(rectangle, wordClip);
          if (exposedRect.right > exposedRect.left && exposedRect.bottom > exposedRect.top) {
            top = Math.min(top, exposedRect.top);
            bottom = Math.max(bottom, exposedRect.bottom);
          }
        }
        visible += portion;
        // Union across words, conservatively retaining the largest observed fraction within one word.
        word.exposure = Math.max(word.exposure, portion);
        exposed += word.exposure;
      }
      if (visible <= 0) continue;
      candidates.push({ key: state.key, wordCount: state.wordCount, requiredMilliseconds: state.requiredMilliseconds,
        creditedMilliseconds: state.creditedMilliseconds, visibleFraction: visible / state.wordCount, exposedFraction: exposed / state.wordCount });
      positionCandidates.push({ key: state.key, top, bottom });
    }
    observation = { session: current, at, lastActivity, candidates, positions: positionCandidates,
      readingLine: viewport.top + (viewport.bottom - viewport.top) / 3 };
  }

  function settleReading(at = now()): void {
    const previous = observation;
    observation = undefined;
    if (!previous) return;
    const current = previous.session;
    const elapsed = allowedReadingElapsed({ now: at, previous: previous.at, lastActivity: previous.lastActivity,
      visible: true, focused: true, printing: false });
    if (current.disabled || elapsed <= 0) {
      if (at > previous.at) current.positionDwell = undefined;
      return;
    }
    // This interval belongs to the previous viewport. A scroll or layout change has already
    // moved the DOM when its event arrives; inspecting it here would credit newly exposed text.
    const candidates = previous.candidates.map(candidate => ({ ...candidate,
      creditedMilliseconds: current.segments.get(candidate.key)?.creditedMilliseconds ?? candidate.creditedMilliseconds }));
    const earned = allocateReadingBudget(candidates, elapsed);
    for (const credit of earned) current.segments.get(credit.key)!.creditedMilliseconds = credit.creditedMilliseconds;
    if (earned.length) scheduleDisplay();
    const advancing = new Set(earned.map(credit => credit.key));
    // Already-read text can be visible on arrival without replacing the saved continuation point.
    const positionKey = selectReadingPosition(previous.positions.filter(candidate => advancing.has(candidate.key)), previous.readingLine);
    const engaged = positionKey ? current.segments.get(positionKey) : undefined;
    current.positionDwell = observeReadingPosition(current.positionDwell, engaged && engaged.creditedMilliseconds > 0 ? engaged.key : undefined, elapsed);
    if (engaged && current.positionDwell?.engaged) {
      const observedAt = Math.max(Math.floor(current.serverClockBase + at - current.performanceClockBase),
        current.position ? Date.parse(current.position.observedAt) + 1 : -Infinity);
      current.position = { nodeId: engaged.nodeId, hash: engaged.hash, segmentIndex: engaged.index, observedAt: new Date(observedAt).toISOString() };
      current.engagedKey = engaged.key;
      current.engagementVersion++;
    }
  }

  async function report(current: Session, keepalive = false): Promise<void> {
    if (!current.manifest || current.disabled || (!keepalive && current.sending)) return;
    const antiforgery = root.querySelector<HTMLMetaElement>('meta[name="request-verification-token"]')?.content;
    if (!antiforgery) return;
    const pending = [...current.segments.values()].filter(state => state.creditedMilliseconds > state.acknowledged);
    const position = current.position && Date.parse(current.position.observedAt) > current.acknowledgedPositionAt ? current.position : undefined;
    const positionState = position ? current.segments.get(`${position.nodeId}/${position.hash}/${position.segmentIndex}`) : undefined;
    // The checkpoint and its positive credit must stay together even when a page has more than 256 pending segments.
    if (positionState && positionState.creditedMilliseconds > 0) {
      const index = pending.indexOf(positionState);
      if (index >= 0) pending.splice(index, 1);
      pending.unshift(positionState);
    }
    const engaged = current.engagedKey ? current.segments.get(current.engagedKey) : undefined;
    if (engaged && engaged.creditedMilliseconds > 0 && !pending.includes(engaged)) pending.push(engaged);
    if (pending.length === 0 && current.pendingViews.size === 0 && (!current.visitRequested || current.visited)) return;
    const viewedBlocks = [...current.pendingViews.values()].slice(0, 64);
    // Keep the combined payload within the browser's keepalive limit on long documents.
    const credits = pending.slice(0, 256 - viewedBlocks.length).map(state => ({ nodeId: state.nodeId, hash: state.hash,
      segmentIndex: state.index, creditedMilliseconds: state.creditedMilliseconds }));
    const batch = current.retry ?? { credits, viewedBlocks, position, body: JSON.stringify({ credits, viewedBlocks, ...(position ? { position } : {}) }),
      engagementVersion: current.engagementVersion, serial: ++current.serial };
    if (!keepalive) current.sending = true;
    current.lastReport = performance.now();
    try {
      const response = await fetch(current.url, { method: "POST", credentials: "same-origin", keepalive: true,
        headers: { "Content-Type": "application/json", RequestVerificationToken: antiforgery }, body: batch.body });
      if (response.status === 401 || response.status === 403) current.disabled = true;
      if (!response.ok) throw new Error("Reading progress was not accepted.");
      const saved = await response.json() as ReadingManifest;
      if (!validManifest(saved, current)) throw new Error("Reading progress response did not match the displayed revision.");
      for (const block of saved.blocks) {
        for (const part of block.segments) {
          const state = current.segments.get(`${block.nodeId}/${block.hash}/${part.index}`);
          if (!state) continue;
          state.creditedMilliseconds = mergeAbsoluteReadingCredit(state.creditedMilliseconds, part.creditedMilliseconds, state.requiredMilliseconds);
          state.acknowledged = mergeAbsoluteReadingCredit(state.acknowledged, part.creditedMilliseconds, state.requiredMilliseconds);
        }
      }
      current.visited = true;
      for (const block of batch.viewedBlocks) current.pendingViews.delete(`${block.nodeId}/${block.hash}`);
      if (batch.position) current.acknowledgedPositionAt = Math.max(current.acknowledgedPositionAt, Date.parse(batch.position.observedAt));
      if (current.retry === batch) current.retry = undefined;
      if (current.engagementVersion <= batch.engagementVersion) current.engagedKey = undefined;
      if (batch.serial >= current.dispatchedSerial && session === current && current.element.isConnected) {
        current.dispatchedSerial = batch.serial;
        emitManifest(current, saved);
      }
    } catch {
      // The same absolute payload is retried; acknowledgements and other tabs never add time twice.
      current.retry ??= batch;
    } finally { if (!keepalive) current.sending = false; }
  }

  async function markAllRead(current: Session): Promise<void> {
    if (marking || !current.manifest || current.disabled) return;
    const token = root.querySelector<HTMLMetaElement>('meta[name="request-verification-token"]')?.content;
    if (!token) return;
    marking = true;
    root.dispatchEvent(new CustomEvent("qprins:reading-mark-status", { detail: { state: "saving" } }));
    settleReading(); captureReading();
    const pending = [...current.segments.values()].filter(state => state.creditedMilliseconds > state.acknowledged)
      .map(state => ({ nodeId: state.nodeId, hash: state.hash, segmentIndex: state.index, creditedMilliseconds: state.creditedMilliseconds }));
    try {
      // Save pending timed observations before the manual event, in bounded batches for long pages.
      for (let offset = 0; offset < Math.max(1, pending.length); offset += 256) {
        const markRead = offset + 256 >= pending.length;
        const response = await fetch(current.url, { method: "POST", credentials: "same-origin",
          headers: { "Content-Type": "application/json", RequestVerificationToken: token },
          body: JSON.stringify({ credits: pending.slice(offset, offset + 256), markRead }) });
        if (!response.ok) throw new Error("The page could not be marked as read. Try again.");
        const saved = await response.json() as ReadingManifest;
        if (!validManifest(saved, current)) throw new Error("The saved reading state did not match this revision. Try again.");
        for (const block of saved.blocks) for (const part of block.segments) {
          const state = current.segments.get(`${block.nodeId}/${block.hash}/${part.index}`);
          if (state) {
            state.creditedMilliseconds = mergeAbsoluteReadingCredit(state.creditedMilliseconds, part.creditedMilliseconds, state.requiredMilliseconds);
            state.acknowledged = mergeAbsoluteReadingCredit(state.acknowledged, part.creditedMilliseconds, state.requiredMilliseconds);
          }
        }
        if (session === current) emitManifest(current, saved);
      }
      root.dispatchEvent(new CustomEvent("qprins:reading-mark-status", { detail: { state: "saved", documentId: current.documentId, revisionId: current.revisionId } }));
    } catch (error) {
      root.dispatchEvent(new CustomEvent("qprins:reading-mark-status", { detail: { state: "error", message: error instanceof Error ? error.message : "Could not save. Try again." } }));
    } finally { marking = false; }
  }

  root.addEventListener("click", event => {
    if (event.target instanceof Element && event.target.closest("[data-reading-mark-all]") && session) void markAllRead(session);
  });

  const mutations = new MutationObserver(records => {
    const current = session;
    const changed = records.some(record => {
      if (record.type === "attributes") return true;
      if (!current || !current.element.contains(record.target)) return true;
      const element = record.target instanceof Element ? record.target : record.target.parentElement;
      if (element?.closest(ignored)) return false;
      const nodes = [...record.addedNodes, ...record.removedNodes];
      return record.type === "characterData" || nodes.length === 0 || nodes.some(node => !(node instanceof Element && node.matches(ignored)));
    });
    if (!changed || mutationScheduled) return;
    settleReading();
    if (current) { current.invalidated = true; current.positionDwell = undefined; }
    mutationScheduled = true;
    queueMicrotask(() => {
      mutationScheduled = false;
      synchronize();
      if (session?.manifest) bind(session);
      captureReading();
      scheduleViews();
    });
  });
  mutations.observe(root.body, { childList: true, subtree: true, characterData: true, attributes: true,
    attributeFilter: ["data-reading-document-id", "data-reading-revision-id", "data-reading-manifest-url", "data-revision-id", "hidden", "contenteditable", "open"] });
  for (const name of ["pointerdown", "pointermove", "keydown", "touchstart"] as const) root.addEventListener(name, activity, { passive: true });
  function refreshViewport(scrolled = false): void {
    settleReading();
    if (scrolled) activity();
    if (session) session.positionDwell = undefined;
    captureReading();
    scheduleViews();
  }
  view.addEventListener("scroll", () => refreshViewport(true), { passive: true, capture: true });
  view.addEventListener("resize", () => refreshViewport(), { passive: true });
  view.visualViewport?.addEventListener("scroll", () => refreshViewport(true), { passive: true });
  view.visualViewport?.addEventListener("resize", () => refreshViewport(), { passive: true });
  root.addEventListener("focusin", () => refreshViewport());
  root.addEventListener("focusout", () => refreshViewport());
  function pauseReading(): void {
    settleReading();
    if (session) session.positionDwell = undefined;
  }
  view.addEventListener("focus", () => { activity(); tick(); });
  view.addEventListener("blur", () => { pauseReading(); if (session) void report(session, true); });
  root.addEventListener("visibilitychange", () => {
    pauseReading();
    if (root.visibilityState === "visible") { activity(); tick(); }
    else if (session) void report(session, true);
  });
  view.addEventListener("beforeprint", () => { pauseReading(); printing = true; if (session) void report(session, true); });
  view.addEventListener("afterprint", () => { printing = false; activity(); tick(); });
  view.addEventListener("pagehide", () => { pauseReading(); suspended = true; if (session) void report(session, true); });
  view.addEventListener("pageshow", () => { suspended = false; activity(); tick(); });
  view.matchMedia("print").addEventListener("change", event => { settleReading(); printing = event.matches; refreshViewport(); });
  view.setInterval(tick, 1000);
  synchronize();
}

function validManifest(value: ReadingManifest, session: Session): boolean {
  return value?.documentId === session.documentId && value.revisionId === session.revisionId && Array.isArray(value.blocks)
    && value.progress !== null && typeof value.progress === "object"
    && value.blocks.every(block => uuid.test(block.nodeId) && typeof block.hash === "string" && typeof block.text === "string"
      && Array.isArray(block.segments) && block.segments.every((segment, index) => segment.index === index
        && Number.isInteger(segment.wordCount) && segment.wordCount > 0 && segment.wordCount <= 24
        && Number.isInteger(segment.requiredMilliseconds) && segment.requiredMilliseconds > 0
        && Number.isInteger(segment.creditedMilliseconds) && segment.creditedMilliseconds >= 0 && segment.creditedMilliseconds <= segment.requiredMilliseconds));
}

export function readingRangesForBlock(element: HTMLElement, expected: string): Range[] | undefined {
  const chunks: { node: Text; start: number; end: number }[] = [];
  let text = "";
  function visit(node: Node): void {
    if (node instanceof Text) {
      chunks.push({ node, start: text.length, end: text.length + node.data.length });
      text += node.data;
    } else if (node instanceof Element) {
      if (node !== element && node.matches(ignored)) return;
      if (node.tagName === "BR") { text += "\n"; return; }
      for (const child of node.childNodes) visit(child);
    }
  }
  visit(element);
  const words = readingWordSpans(text);
  const expectedWords = readingWordSpans(expected);
  if (words.length !== expectedWords.length || words.some((word, index) => word.text !== expectedWords[index].text)) return;
  let firstChunk = 0;
  const ranges: Range[] = [];
  for (const word of words) {
    while (firstChunk < chunks.length && chunks[firstChunk].end <= word.start) firstChunk++;
    let lastChunk = firstChunk;
    while (lastChunk < chunks.length && chunks[lastChunk].end < word.end) lastChunk++;
    const first = chunks[firstChunk], last = chunks[lastChunk];
    if (!first || !last) return;
    const range = element.ownerDocument.createRange();
    range.setStart(first.node, word.start - first.start);
    range.setEnd(last.node, word.end - last.start);
    ranges.push(range);
  }
  return ranges;
}

export function readingRangeForSegment(element: HTMLElement, block: ReadingBlock, index: number): Range | undefined {
  const segment = block.segments.find(item => item.index === index);
  const words = segment ? readingRangesForBlock(element, block.text) : undefined;
  if (!segment || !words || words.length !== block.segments.reduce((sum, item) => sum + item.wordCount, 0)) return;
  const offset = block.segments.filter(item => item.index < index).reduce((sum, item) => sum + item.wordCount, 0);
  const first = words[offset];
  const last = words[offset + segment.wordCount - 1];
  if (!first || !last) return;
  const range = first.cloneRange();
  range.setEnd(last.endContainer, last.endOffset);
  return range;
}

function readingViewport(root: Document): ReadingRect {
  const view = root.defaultView!;
  const visual = view.visualViewport;
  const header = root.querySelector<HTMLElement>(".workspace-header")?.getBoundingClientRect();
  return { left: visual?.offsetLeft ?? 0, top: Math.max(visual?.offsetTop ?? 0, header && header.top <= 0 ? header.bottom : 0),
    right: (visual?.offsetLeft ?? 0) + (visual?.width ?? root.documentElement.clientWidth), bottom: (visual?.offsetTop ?? 0) + (visual?.height ?? view.innerHeight) };
}

function visibleClip(element: Element, root: Document): ReadingRect | undefined {
  const view = root.defaultView!;
  let clip = readingViewport(root);
  for (let parent: Element | null = element; parent; parent = parent.parentElement) {
    if (parent.hasAttribute("hidden") || parent.getAttribute("aria-hidden") === "true") return;
    if (parent instanceof HTMLDetailsElement && !parent.open && !parent.querySelector(":scope > summary")?.contains(element)) return;
    const style = view.getComputedStyle(parent);
    if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse" || style.contentVisibility === "hidden" || Number(style.opacity) === 0) return;
    const x = /^(hidden|clip|auto|scroll)$/.test(style.overflowX);
    const y = /^(hidden|clip|auto|scroll)$/.test(style.overflowY);
    if (x || y) {
      const bounds = parent.getBoundingClientRect();
      clip = intersectReadingRects(clip, { left: x ? bounds.left + parent.clientLeft : clip.left,
        right: x ? bounds.left + parent.clientLeft + parent.clientWidth : clip.right,
        top: y ? bounds.top + parent.clientTop : clip.top, bottom: y ? bounds.top + parent.clientTop + parent.clientHeight : clip.bottom });
    }
  }
  return clip.right > clip.left && clip.bottom > clip.top ? clip : undefined;
}
